import { Injectable } from '@angular/core';
import { AppConsts } from '../../shared/AppConsts';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, filter, finalize, mergeMap, switchMap, take } from 'rxjs/operators';
import { Platform } from '@ionic/angular';
import { AuthenticateResultModel } from 'openapi';
import { SSOAuthService } from './sso.service';
import { AppToasterService } from '@shared/service/app-toaster.service';
import { StringUtils } from '@shared/utils/StringUtils';
import { CookieAuthService } from './cookie-auth.service';

@Injectable()
export class CamsAuthService {

    private defaultHeaders = new HttpHeaders();
    private responseType = 'json';
    private withCredentials = true;

    
    tokenRefreshInProgress = false;
    tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    
    private _user: any;
    private _tenant: any;
    private _application: any;

    constructor(
        private _httpClient: HttpClient,
        private _cookieService: CookieService,
        private platform: Platform,
        private ssoAuthService: SSOAuthService,
        private notify: AppToasterService,
        private sessionService: CookieAuthService,
    ) {
        this.defaultHeaders = this.defaultHeaders.set('Accept', 'application/json');
    }

    get application(): any {
        return this._application;
    }

    get user(): any {
        return this._user;
    }

    set resetUser(reset: boolean) {
        this._user = null;
    }

    get userId(): number {
        return this.user ? this.user.id : null;
    }

    get tenant(): any {
        return this._tenant;
    }

    get tenantId(): number {
        return this.tenant ? this.tenant.id : null;
    }

    ensureLoggedIn(tenantId): Observable<any> {
        var token = this._cookieService.get(AppConsts.authorization.AuthToken);
        if (token) {
            return of(true);
        } else {
            if (this.tokenRefreshInProgress) {
                return this.tokenSubject.pipe(
                    filter(authUser => authUser != null),
                    take(1)
                );
            } else {
                this.tokenRefreshInProgress = true;
                this.tokenSubject.next(null);
                return from(this.authenticate(tenantId)).pipe(switchMap(result => {
                    this.tokenSubject.next(true);
                    if (result) return of(true);
                    return of(false);
                })).pipe(catchError((error) => {
                    return of(false);
                })).pipe(
                    finalize(() => {
                        this.tokenRefreshInProgress = false;
                    }));
            }
        }
    }

    logout(){
        this._cookieService.delete(AppConsts.authorization.encryptedAuthTokenName);
        this._cookieService.delete(AppConsts.authorization.AuthToken);
    }

    authenticate(tenantId) {
        return new Promise<AuthenticateResultModel | null>(async (resolve, reject) => {
            // On web the Wak.Token cookie is httpOnly and never readable here, so the
            // browser sends it with the request and cams-api decides (#2903). The
            // mobile app still holds its own copy, so it keeps the local check.
            const isMobile = StringUtils.isMobilePlatform(this.platform);
            const token = isMobile ? await this.sessionService.getCookie(AppConsts.SSO.token) : null;
            if (!isMobile || token) {
                let basepath = "";
                if (isMobile)
                  basepath = AppConsts.remoteServiceBaseUrl.replace('/api', '');
                this._httpClient.post<AuthenticateResultModel>(basepath + `/cams-service/token/authenticate`, {
                    tenantId: tenantId,
                    superAdmin: false,
                    admin: false,
                    client: true
                }, {
                    withCredentials: this.withCredentials,
                    responseType: <any>this.responseType,
                    headers: this.defaultHeaders,
                }).subscribe({
                    next: (response) => {
                        if(response.userId != null && response.userId > 0)
                            resolve(response);
                        else {
                            this.notify.error(response.decription)
                            // Clearing the cookies to remove user session
                            const host = StringUtils.isMobilePlatform(this.platform) ? 'wakandi.com' : window.location.host;
                            this._cookieService.deleteAll("/", host, true, "None");
                            this._cookieService.deleteAll("/", host, true, "Lax");
                             // Auth failed (no member profile): clear session and send user to SSO login
                             //this.ssoAuthService.logout();
                            resolve(null)
                        }
                    }, error: (error) => {
                        // Auth failed (no member profile): clear session and send user to SSO login
                        //this.ssoAuthService.logout();
                        resolve(null);
                    }
                });
            } else {
                resolve(null);
            }
        });
    }
}